/**
 * Stok Referans Bütünlüğü — Detay (drill-down, salt-okunur)
 *
 * Panelde bir sayıma (orphan/bagli/sentinel/legacy/gecersiz) tıklanınca açılır.
 * Backend: GET /api/stok/referans-butunlugu/detay?tip=&sinif= (MANAGE_SETTINGS).
 * Aksiyon YOK (M.6) — satırları gösterir + CSV/kopyala ile dışa aktarır; karar kullanıcının.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../../components/Header';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../auth/AuthProvider';
import { PERMISSIONS, hasPermission } from '../../auth/permissions';
import { colors, radius } from '../../styles/theme';
import useGoBack from '../../hooks/useGoBack';
import useUrlState from '../../hooks/useUrlState';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const SINIF_ETIKET = {
  orphan: 'Orphan (kaynak silinmiş)',
  bagli: 'Bağlı',
  sentinel: 'Sentinel (kaynaksız, referans_id=0)',
  legacy: 'Legacy (eski sistem, referans_id=0)',
  gecersiz: 'Geçersiz (negatif)',
};

const KOLONLAR = [
  ['hareket_no', 'Hareket No'],
  ['hareket_tipi', 'Hareket Tipi'],
  ['malzeme_ad', 'Malzeme'],
  ['lokasyon_ad', 'Lokasyon'],
  ['miktar', 'Miktar'],
  ['birim_maliyet', 'Birim Maliyet'],
  ['referans_id', 'referans_id'],
  ['hareket_tarihi', 'Tarih'],
  ['aciklama', 'Açıklama'],
];

const fmtTarih = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleString('tr-TR');
};

const StokReferansButunluguDetayPage = () => {
  const { user } = useAuth();
  const toast = useToast();
  const goBack = useGoBack('/ayarlar/stok-referans-butunlugu');
  const [params] = useSearchParams();
  const tip = params.get('tip') || '';
  const sinif = params.get('sinif') || '';

  const canView = hasPermission(
    user?.role,
    PERMISSIONS.MANAGE_SETTINGS,
    user?.effectivePermissions,
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useUrlState('q', '', { debounceMs: 300 });

  useEffect(() => {
    if (!canView || !tip || !sinif) {
      setLoading(false);
      return;
    }
    let iptal = false;
    (async () => {
      setLoading(true);
      try {
        const url = `${API_URL}/api/stok/referans-butunlugu/detay?tip=${encodeURIComponent(
          tip,
        )}&sinif=${encodeURIComponent(sinif)}`;
        const res = await fetch(url, { headers: authHeaders() });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        if (!iptal) setData(body);
      } catch (err) {
        logError('Detay yüklenemedi:', err);
        if (!iptal) toast.error('Detay yüklenemedi');
      } finally {
        if (!iptal) setLoading(false);
      }
    })();
    return () => {
      iptal = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, tip, sinif]);

  if (!canView) {
    return (
      <div>
        <Header title="Referans Bütünlüğü — Detay" />
        <div style={{ padding: 40, textAlign: 'center', color: colors.textSecondary }}>
          Bu sayfayı görüntüleme yetkiniz yok. (MANAGE_SETTINGS gerekli)
        </div>
      </div>
    );
  }

  const tumSatirlar = data?.satirlar || [];
  const aramaLower = (q || '').toLocaleLowerCase('tr-TR');
  const satirlar = aramaLower
    ? tumSatirlar.filter((s) =>
        [s.malzeme_ad, s.lokasyon_ad, s.hareket_no, s.aciklama, String(s.referans_id)]
          .filter(Boolean)
          .some((x) => String(x).toLocaleLowerCase('tr-TR').includes(aramaLower)),
      )
    : tumSatirlar;

  const csvIndir = () => {
    const head = KOLONLAR.map(([, l]) => l).join(';');
    const lines = satirlar.map((s) =>
      KOLONLAR.map(([k]) => {
        const v = k === 'hareket_tarihi' ? fmtTarih(s[k]) : s[k];
        const str = v == null ? '' : String(v).replace(/"/g, '""');
        return /[;"\n]/.test(str) ? `"${str}"` : str;
      }).join(';'),
    );
    const csv = '﻿' + [head, ...lines].join('\n'); // BOM → Excel TR uyumu
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `referans-butunlugu_${tip}_${sinif}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const kopyala = async () => {
    const head = KOLONLAR.map(([, l]) => l).join('\t');
    const lines = satirlar.map((s) =>
      KOLONLAR.map(([k]) => (k === 'hareket_tarihi' ? fmtTarih(s[k]) : s[k] ?? '')).join('\t'),
    );
    try {
      await navigator.clipboard.writeText([head, ...lines].join('\n'));
      toast.success(`${satirlar.length} satır kopyalandı`);
    } catch {
      toast.error('Kopyalanamadı');
    }
  };

  const baslik = `${tip} / ${SINIF_ETIKET[sinif] || sinif}`;
  const th = { padding: 10, textAlign: 'left', fontSize: 12, color: colors.textSecondary, whiteSpace: 'nowrap' };
  const td = { padding: 10, fontSize: 13, borderTop: `1px solid ${colors.border}` };
  const btn = {
    padding: '8px 14px',
    border: `1px solid ${colors.border}`,
    borderRadius: radius.sm,
    background: '#fff',
    cursor: 'pointer',
    fontSize: 13,
  };

  return (
    <div>
      <Header title="Referans Bütünlüğü — Detay" />
      <div style={{ padding: 20, background: colors.background, minHeight: 'calc(100vh - 60px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          <button onClick={goBack} style={btn}>← Geri</button>
          <h2 style={{ margin: 0, fontSize: 18, color: colors.textPrimary }}>{baslik}</h2>
          {data && (
            <span style={{ color: colors.textSecondary, fontSize: 13 }}>
              {data.toplam} kayıt
              {data.toplam > tumSatirlar.length ? ` (ilk ${tumSatirlar.length} gösteriliyor)` : ''}
            </span>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button onClick={kopyala} style={btn} disabled={!satirlar.length}>⧉ Kopyala</button>
            <button onClick={csvIndir} style={btn} disabled={!satirlar.length}>⬇ CSV</button>
          </div>
        </div>

        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Malzeme / lokasyon / hareket no / açıklama ara…"
          style={{
            width: '100%',
            maxWidth: 420,
            padding: '9px 12px',
            border: `1px solid ${colors.border}`,
            borderRadius: radius.sm,
            fontSize: 13,
            marginBottom: 16,
          }}
        />

        {loading && !data ? (
          <div style={{ padding: 40, textAlign: 'center', color: colors.textSecondary }}>Yükleniyor…</div>
        ) : !satirlar.length ? (
          <div style={{ padding: 40, textAlign: 'center', color: colors.textSecondary }}>
            {q ? 'Aramayla eşleşen kayıt yok.' : 'Kayıt yok.'}
          </div>
        ) : (
          <div style={{ background: '#fff', border: `1px solid ${colors.border}`, borderRadius: radius.md, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: colors.background }}>
                <tr>{KOLONLAR.map(([k, l]) => <th key={k} style={th}>{l}</th>)}</tr>
              </thead>
              <tbody>
                {satirlar.map((s) => (
                  <tr key={s.id}>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{s.hareket_no}</td>
                    <td style={td}>{s.hareket_tipi}</td>
                    <td style={td}>{s.malzeme_ad || `#${s.malzeme_id}`}</td>
                    <td style={td}>{s.lokasyon_ad || `#${s.lokasyon_id}`}</td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{s.miktar}</td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{s.birim_maliyet ?? '—'}</td>
                    <td style={{ ...td, textAlign: 'right', color: sinif === 'orphan' ? '#c62828' : colors.textPrimary }}>{s.referans_id}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtTarih(s.hareket_tarihi)}</td>
                    <td style={{ ...td, color: colors.textSecondary }}>{s.aciklama || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StokReferansButunluguDetayPage;
